import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/services/supabaseClient";
import type {
  Tables,
  InsertTables,
  UpdateTables,
  EventStatus,
} from "@/types/database";

interface EventFilters {
  status?: EventStatus;
  teamId?: string;
  coordinatorId?: string;
}

export type EventWithRelations = Tables<"events"> & {
  team?: Tables<"teams"> | null;
  client?: Tables<"clients"> | null;
  organizer?: Tables<"organizers"> | null;
  coordinator?: { full_name: string | null } | null;
};

async function fetchEvents(filters: EventFilters = {}) {
  let query = supabase.from("events").select(`
      *,
      team:teams(*),
      client:clients(*),
      organizer:organizers(*),
      coordinator:users!events_coordinator_id_fkey(full_name)
    `);

  if (filters.status) query = query.eq("status", filters.status);
  if (filters.teamId) query = query.eq("team_id", filters.teamId);
  if (filters.coordinatorId)
    query = query.eq("coordinator_id", filters.coordinatorId);

  const { data, error } = await query.order("event_date", { ascending: true });

  if (error) {
    console.error("Error fetching events:", error);
    throw error;
  }
  console.log("Fetched events:", data?.length);
  return data as EventWithRelations[];
}

async function fetchEventById(id: string) {
  const { data, error } = await supabase
    .from("events")
    .select(
      `
      *,
      team:teams(*),
      client:clients(*),
      organizer:organizers(*),
      coordinator:users!events_coordinator_id_fkey(full_name),
      quote:quotes(*),
      technical_sheet:technical_sheets(*)
    `,
    )
    .eq("id", id)
    .single();

  if (error) throw error;
  return data;
}

async function createEvent(event: InsertTables<"events">) {
  const { data, error } = await supabase
    .from("events")
    .insert([event])
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function updateEvent(id: string, updates: UpdateTables<"events">) {
  const { data, error } = await supabase
    .from("events")
    .update(updates)
    .eq("id", id)
    .select()
    .single();

  if (error) throw error;
  return data;
}

async function deleteEvent(id: string) {
  const { error } = await supabase.from("events").delete().eq("id", id);

  if (error) throw error;
}

export function useEvents(filters: EventFilters = {}) {
  return useQuery({
    queryKey: ["events", filters],
    queryFn: () => fetchEvents(filters),
  });
}

export function useEvent(id: string) {
  return useQuery({
    queryKey: ["event", id],
    queryFn: () => fetchEventById(id),
    enabled: !!id,
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      updates,
    }: {
      id: string;
      updates: UpdateTables<"events">;
    }) => updateEvent(id, updates),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      queryClient.invalidateQueries({ queryKey: ["event", id] });
    },
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEvent,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
}
